import {
  selectEffectiveChannelState,
  selectProductIsBusy,
  selectResolvedVariantForView,
  selectSelectedListingProductId,
} from '../../store/selectors/productSelectors';
import { hasConnectedShopifyStore } from '../../components/teammates/views/Channels/helpers/salesChannelState';

import Logger from '../Logger';

export const parseProductRoute = (pathname = '') => {
  const match = String(pathname).match(/^\/product\/([^/]+)(?:\/([^/]+))?\/?$/);
  if (!match) return null;

  return {
    productId: match[1],
    variantId: match[2] || null,
  };
};

export const isOnProductRoute = (pathname) => Boolean(parseProductRoute(pathname));

export const collectEventProductIds = (assets = []) => {
  const ids = new Set();

  assets.forEach((asset) => {
    (asset.attachedProducts || []).forEach((product) => {
      if (product?.masterProductId) ids.add(String(product.masterProductId));
      if (product?._id) ids.add(String(product._id));
    });
    (asset.attachedProductVariants || []).forEach((variant) => {
      if (variant?.masterProductId) ids.add(String(variant.masterProductId));
      if (variant?.productId) ids.add(String(variant.productId));
    });
  });

  return ids;
};

const getListingIdsForMaster = (productsState, masterProductId) => (
  Object.values(productsState?.entities?.listings || {})
    .filter((listing) => String(listing?.masterProductId) === String(masterProductId))
    .map((listing) => listing?._id)
    .filter(Boolean)
    .map(String)
);

export const isCurrentProductAttached = (pathname, assets, productsState) => {
  const route = parseProductRoute(pathname);
  if (!route) return false;

  const eventIds = collectEventProductIds(assets);
  if (!eventIds.size) return false;
  if (eventIds.has(String(route.productId))) return true;

  return getListingIdsForMaster(productsState, route.productId)
    .some((listingId) => eventIds.has(listingId));
};

const unwrapAction = (action) => (
  action && typeof action.unwrap === 'function' ? action.unwrap() : action
);

const refreshCurrentProductMedia = async ({
  store,
  productsActions,
  assets,
}) => {
  const state = store.getState();
  const pathname = state.router?.location?.pathname || '';
  if (!isCurrentProductAttached(pathname, assets, state.products)) return;

  const route = parseProductRoute(pathname);
  if (!route) return;

  const { productId, variantId } = route;

  if (selectProductIsBusy(state, productId)) return;

  try {
    const { contextChannelId, isMasterView } = selectEffectiveChannelState(
      state,
      productId,
      variantId,
    ) || {};
    const listingProductId = selectSelectedListingProductId(state, productId);
    const resolvedVariant = selectResolvedVariantForView(state, productId, variantId);
    const isShopifyConnected = hasConnectedShopifyStore(state.user?.team);
    const tasks = [];

    if (isMasterView) {
      tasks.push(productsActions.fetchAssets({ productId }));
      if (variantId) {
        tasks.push(productsActions.fetchVariantAssets({ variantId, productId }));
      }
    } else if (isShopifyConnected) {
      if (!variantId && listingProductId && contextChannelId) {
        tasks.push(productsActions.fetchListingAssets({
          masterProductId: productId,
          channelId: contextChannelId,
          listingProductId,
        }));
      }

      if (
        variantId
        && resolvedVariant?.linkVariantId
        && contextChannelId
        && resolvedVariant?.listingId
      ) {
        tasks.push(productsActions.fetchVariantAssets({
          productId,
          variantId: resolvedVariant.linkVariantId,
          channelId: contextChannelId,
          masterVariantId: variantId,
        }));
      }
    }

    if (!tasks.length) return;

    await Promise.all(tasks.map(unwrapAction));
  } catch (error) {
    Logger.error(error instanceof Error
      ? error
      : new Error('Error refreshing product media after attach'), {
      error,
      productId,
    }, [
      'RefreshProductMediaAfterAttachFailed',
      {
        productId: String(productId || ''),
        errorMessage: error?.message || 'NoMessage',
      },
    ]);
  }
};

const showAttachingAssetsCompleteToast = ({
  store,
  productsActions,
  Toast,
  l18n,
  assets,
}) => {
  const state = store.getState();
  const pathname = state.router?.location?.pathname || '';
  const isStillOnAttachedProduct = isCurrentProductAttached(
    pathname,
    assets,
    state.products,
  );

  Toast(l18n.TOAST.ATTACHING_ASSETS_TO_PRODUCT_COMPLETE, {
    autoClose: false,
    closeButton: false,
    type: 'success',
    ...(isStillOnAttachedProduct && {
      btnOkValue: l18n.HISTORY.textBtnRefresh,
      onOk: () => refreshCurrentProductMedia({
        store,
        productsActions,
        assets,
      }),
    }),
  });
};

export default showAttachingAssetsCompleteToast;
